import { useState } from 'react';

import { Flex, HStack, Text } from '@chakra-ui/react';
import { ArrowDownBold, ArrowUpBold } from '@aslanpay/portal-common-lib';
import { type ColumnDef, type PaginationState } from '@tanstack/react-table';

import { Table } from '../Table';

export interface SummaryTableSection {
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<any, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
}

interface SummaryTableProps {
  sections: SummaryTableSection[];
  pageSize?: number;
}

interface SectionState {
  isExpanded: boolean;
  pageIndex: number;
  pageSize: number;
}

/**
 * Renders a set of collapsible, paginated tables, one per change category (joiners, salary
 * changes, name changes, etc.) — used by the EDF/gifting-EDF confirm screens to surface fps
 * analysis results. Sections with no rows are omitted entirely.
 */
function SummaryTable({ sections, pageSize: defaultPageSize = 15 }: SummaryTableProps) {
  const [sectionStates, setSectionStates] = useState<SectionState[]>(() =>
    sections.map(() => ({ isExpanded: false, pageIndex: 0, pageSize: defaultPageSize }))
  );

  const toggleExpanded = (index: number) => {
    setSectionStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, isExpanded: !s.isExpanded } : s))
    );
  };

  const handlePaginationChange =
    (index: number): React.Dispatch<React.SetStateAction<PaginationState>> =>
    (updater) => {
      setSectionStates((prev) =>
        prev.map((s, i) => {
          if (i !== index) return s;
          const newPagination =
            typeof updater === 'function'
              ? updater({ pageIndex: s.pageIndex, pageSize: s.pageSize })
              : updater;
          return { ...s, pageIndex: newPagination.pageIndex, pageSize: newPagination.pageSize };
        })
      );
    };

  if (sections.every((s) => s.data.length === 0)) return null;

  return (
    <Flex flexDir="column">
      {sections.map((section, index) => {
        if (section.data.length === 0) return null;

        const { isExpanded, pageIndex, pageSize } = sectionStates[index];
        const pagedData = section.data.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

        return (
          <Flex key={section.title} flexDir="column">
            <HStack
              cursor="pointer"
              onClick={() => toggleExpanded(index)}
              justify="space-between"
              py="12px"
              borderTop="1px solid"
              borderColor="brand.lightGray1"
            >
              <Text textStyle="smallSemiBold" color="brand.purple">
                {section.title}{' '}
                <Text as="span" color="brand.mutedPurple" textStyle="smallRegular">
                  ({section.data.length})
                </Text>
              </Text>
              {isExpanded ? (
                <ArrowUpBold boxSize="16px" color="brand.purple" />
              ) : (
                <ArrowDownBold boxSize="16px" color="brand.purple" />
              )}
            </HStack>

            {isExpanded && (
              <Flex flexDir="column" pb="10px">
                <Table
                  columns={section.columns}
                  data={pagedData}
                  pageNumber={pageIndex}
                  pageSize={pageSize}
                  total={section.data.length}
                  sorting={[]}
                  onPaginationChange={handlePaginationChange(index)}
                  onSortingChange={() => {}}
                  showHover={false}
                />
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

export default SummaryTable;
